/**
 * Favorites — hybrid store.
 *
 * • Signed-out users: localStorage (per-device).
 * • Signed-in users: Firestore under users/{uid}/favorites (syncs across
 *   every device the same account signs in on).
 *
 * The active uid is set by `UserAuthProvider` via {@link setActiveUid}
 * whenever Firebase auth state changes. The first sign-in also migrates any
 * localStorage favorites the user built up while signed-out.
 */
import { useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { getDb } from "./firebase";

const KEY = "rawan-favorites-v1";

export type FavoriteRef = { slug: string; kind: "movie" | "series"; addedAt: number };

type Listener = () => void;
const listeners = new Set<Listener>();

let _uid: string | null = null;
let _remote: FavoriteRef[] = [];
let _unsub: (() => void) | null = null;

function readLocal(): FavoriteRef[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as FavoriteRef[]) : [];
  } catch {
    return [];
  }
}
function writeLocal(next: FavoriteRef[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
}
function notify(): void {
  listeners.forEach((l) => l());
}

/** Called by UserAuthProvider whenever the signed-in user changes. */
export function setActiveUid(uid: string | null): void {
  if (uid === _uid) return;
  if (_unsub) {
    try { _unsub(); } catch { /* ignore */ }
    _unsub = null;
  }
  _uid = uid;
  _remote = [];

  const db = getDb();
  if (!uid || !db) {
    notify();
    return;
  }

  // On first sign-in, migrate any local favorites the user built while
  // signed-out into their remote list (merge, don't overwrite).
  const local = readLocal();
  if (local.length > 0) {
    const batch = writeBatch(db);
    for (const f of local) {
      batch.set(
        doc(db, "users", uid, "favorites", f.slug),
        { kind: f.kind, addedAt: f.addedAt || Date.now() },
        { merge: true },
      );
    }
    batch.commit().then(() => {
      try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    }).catch(() => { /* ignore */ });
  }

  const c = collection(db, "users", uid, "favorites");
  _unsub = onSnapshot(query(c, orderBy("addedAt", "desc"), limit(500)), (snap) => {
    const out: FavoriteRef[] = [];
    snap.forEach((s) => {
      const d = s.data() as { kind?: "movie" | "series"; addedAt?: number };
      out.push({ slug: s.id, kind: (d.kind ?? "movie"), addedAt: d.addedAt ?? 0 });
    });
    _remote = out;
    notify();
  }, () => { /* subscription error, ignore */ });
}

export function getFavorites(): FavoriteRef[] {
  return _uid ? _remote : readLocal();
}
export function isFavorite(slug: string): boolean {
  return getFavorites().some((f) => f.slug === slug);
}

export function toggleFavorite(slug: string, kind: FavoriteRef["kind"]): boolean {
  const uid = _uid;
  const db = getDb();
  if (uid && db) {
    const ref = doc(db, "users", uid, "favorites", slug);
    const exists = _remote.some((f) => f.slug === slug);
    if (exists) {
      // Optimistic local update.
      _remote = _remote.filter((f) => f.slug !== slug);
      notify();
      deleteDoc(ref).catch(() => { /* onSnapshot will resync */ });
      return false;
    }
    _remote = [{ slug, kind, addedAt: Date.now() }, ..._remote];
    notify();
    // Best-effort mirror to thread-watchers (kept in firebase-data helper).
    setDoc(ref, { kind, addedAt: Date.now() }).catch(() => { /* ignore */ });
    // Fire-and-forget watcher index update.
    void getDoc(ref).catch(() => {});
    return true;
  }
  const cur = readLocal();
  const exists = cur.find((f) => f.slug === slug);
  if (exists) {
    writeLocal(cur.filter((f) => f.slug !== slug));
    notify();
    return false;
  }
  writeLocal([{ slug, kind, addedAt: Date.now() }, ...cur]);
  notify();
  return true;
}

export function useFavorites(): FavoriteRef[] {
  const [, tick] = useState(0);
  useEffect(() => {
    const l: Listener = () => tick((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return getFavorites();
}
